/**
 * CARD BUILDING BLOCKS — glass and paper surfaces distilled from ParcelCard
 * (aesthetic L2965–3015) so features compose them instead of copying it (owner S3).
 *
 *   <GlassPanel>  a surface: dusk glass, day glass, paper or earth, with the card
 *                 radius and an optional .ld-in arrival (settle in, leave at 0.7×)
 *   <Facts>       the .ld-facts grid of display figures with tracked-caps labels
 *   <Fig>         one display-serif tabular figure with a small unit: 12.4 km
 *   <Meta>        a tracked-caps meta line
 *
 * Read on paper, look through glass: anything over the map is glass, anything
 * read closely is paper. The styling lives in css/cards.css, base.css and views.css (S1).
 */
import type { CSSProperties, HTMLAttributes, JSX, ReactNode, Ref } from "react";

const cx = (...a: Array<string | false | null | undefined>): string => a.filter(Boolean).join(" ");

/** CSSProperties plus custom properties (--fx, --fy, …). */
type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

/* ── GlassPanel ─────────────────────────────────────────────────────── */

export type Surface = "glass" | "glass-day" | "paper" | "earth";

const SURFACE_CLASS: Record<Surface, string> = {
  glass: "ld-glass", //              warm dusk glass over the scene
  "glass-day": "ld-glass--day", //   day glass for bright scenes (frost added below)
  paper: "ld-paper", //              ivory with ridge grain, for reading
  earth: "ld-earth", //              solid dark, print-like moments
};

// Day glass without the .ld-glass class: the descendant rules for dusk glass
// (.ld-glass .ld-meta / .ld-cap / .ld-facts / .ld-status → ivory ink) would
// otherwise put light text on the light surface. The frost .ld-glass carries is added inline.
const DAY_FROST: CSSProperties = { WebkitBackdropFilter: "blur(22px) saturate(1.25)", backdropFilter: "blur(22px) saturate(1.25)" };

type Enter = "right" | "left" | "up";

// .ld-in / .ld-out read --fx/--fy. css/motion.css ships --right and --up; left mirrors right here.
const ENTER_CLASS: Record<Enter, string | null> = { right: "ld-in--right", up: "ld-in--up", left: null };
const LEFT_VARS: CSSVars = { "--fx": "-28px", "--fy": "0px" };

export interface GlassPanelProps extends Omit<HTMLAttributes<HTMLElement>, "className" | "style" | "children"> {
  as?: "aside" | "section" | "div";
  surface?: "glass" | "glass-day" | "paper" | "earth";
  className?: string;
  style?: CSSProperties;
  label?: string; // aria-label
  enter?: "right" | "left" | "up" | null; // .ld-in variants
  children?: ReactNode;
  /** Play the exit instead (.ld-out: the same direction, 0.7 of the entry duration, leave curve). */
  leaving?: boolean;
  /** Stagger index for the arrival (--i × 40 ms). */
  index?: number;
  /** Drop the card shape (.ld-card: 22 px radius, clipped) when the caller shapes the panel itself. */
  bare?: boolean;
  ref?: Ref<HTMLElement>;
}

export function GlassPanel({
  as: Tag = "div",
  surface = "glass",
  className,
  style,
  label,
  enter = null,
  leaving = false,
  index,
  bare = false,
  children,
  ref,
  ...rest
}: GlassPanelProps): JSX.Element {
  const dir = enter ? ENTER_CLASS[enter] : null;
  const motion = leaving ? "ld-out" : enter ? "ld-in" : null;
  const vars: CSSVars = {
    ...(surface === "glass-day" ? DAY_FROST : null),
    ...(enter === "left" ? LEFT_VARS : null),
    ...(index != null ? { "--i": index } : null),
    ...style,
  };
  return (
    <Tag
      ref={ref as Ref<HTMLDivElement>}
      className={cx(SURFACE_CLASS[surface], !bare && "ld-card", motion, dir, className)}
      style={vars}
      aria-label={label}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ── Fig ────────────────────────────────────────────────────────────── */

export interface FigProps {
  value: ReactNode;
  /** Small tracked caps after the figure: "km", "min". */
  unit?: string;
  /** Small serif before the figure: "₹", "~". */
  pre?: string;
  className?: string;
  style?: CSSProperties;
}

/** Display-serif tabular figure: <Fig value={12.4} unit="km" /> */
export function Fig({ value, unit, pre, className, style }: FigProps): JSX.Element {
  return (
    <span className={cx("ld-fig ld-num", className)} style={style}>
      {pre && <small className="pre">{pre}</small>}
      {value}
      {unit && <small>{unit}</small>}
    </span>
  );
}

/* ── Facts ──────────────────────────────────────────────────────────── */

export interface FactItem { value: ReactNode; unit?: string; pre?: string; label: string }

export interface FactsProps {
  items: FactItem[];
  className?: string;
  /** Columns. Default: one per item up to four (css/cards.css lays out three). */
  cols?: number;
  style?: CSSProperties;
}

/** The .ld-facts 3-up grid with .ld-fig display figures. */
export function Facts({ items, className, cols, style }: FactsProps): JSX.Element {
  const n = cols ?? (items.length > 0 && items.length <= 4 ? items.length : 3);
  const grid: CSSProperties | undefined = n === 3 ? style : { gridTemplateColumns: `repeat(${n},minmax(0,1fr))`, ...style };
  return (
    <div className={cx("ld-facts", className)} style={grid}>
      {items.map((it, i) => (
        <div key={`${i}:${it.label}`}>
          <Fig value={it.value} unit={it.unit} pre={it.pre} />
          <span className="ld-fact-l">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Meta ───────────────────────────────────────────────────────────── */

export interface MetaProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  as?: "span" | "div" | "p";
  id?: string;
}

/** Tracked caps meta line. */
export function Meta({ children, className, style, as: Tag = "span", id }: MetaProps): JSX.Element {
  return (
    <Tag className={cx("ld-meta", className)} style={style} id={id}>
      {children}
    </Tag>
  );
}
